// src/pages/Results.jsx — v4c
import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import {
  Box, Paper, Typography, Stack, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Button, CircularProgress, Alert, alpha, IconButton, Tooltip,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon      from "@mui/icons-material/Cancel";
import SchoolIcon      from "@mui/icons-material/School";
import ReplayIcon           from "@mui/icons-material/Replay";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import DownloadIcon         from "@mui/icons-material/Download";
import API from "../api";
import PaginationBar from "../components/PaginationBar";
import { P, getPct, pctColor } from "../theme";

export default function Results() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const fresh    = location.state;

  const [items,    setItems]    = useState([]);
  const [total,    setTotal]    = useState(0);
  const [pages,    setPages]    = useState(1);
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading,  setLoading]  = useState(false);
  const [exams,    setExams]    = useState([]);

  useEffect(() => { document.title = "Mes résultats — ExamLabs"; }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, eRes] = await Promise.all([
        API.get(`/attempts/?page=${page}&page_size=${pageSize}`),
        API.get("/exams/?page_size=100"),
      ]);
      const aData    = aRes.data;
      const rawItems = Array.isArray(aData) ? aData : (aData.items || []);
      setItems(rawItems);
      setTotal(Array.isArray(aData) ? rawItems.length : (aData.total || rawItems.length));
      setPages(Array.isArray(aData) ? 1 : (aData.pages || 1));
      setExams(Array.isArray(eRes.data) ? eRes.data : (eRes.data.items || []));
    } catch { }
    finally { setLoading(false); }
  }, [page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const examTitle     = id => exams.find(e => e.id === id)?.title || `Exam #${id}`;
  const examThreshold = id => exams.find(e => e.id === id)?.pass_threshold ?? 50;

  return (
    <Box sx={{ maxWidth: 860, mx: "auto" }}>

      {/* Header */}
      <Box sx={{
        background: P.navy, color: "white",
        px: { xs: 3, md: 5 }, py: { xs: 3, md: 4 },
        borderRadius: "16px", mb: 3,
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: "1.4rem", letterSpacing: "-0.01em" }}>
              {t("results.title")}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,.65)", fontSize: "0.88rem", mt: .25 }}>
              {total} {total !== 1 ? t("results.attempts") : t("results.attempt")} — {t("results.subtitle")}
            </Typography>
          </Box>
          <Button component={Link} to="/exams" startIcon={<SchoolIcon />} variant="contained"
            sx={{ background: "rgba(255,255,255,.15)", "&:hover": { background: "rgba(255,255,255,.25)" },
              textTransform: "none", fontWeight: 700, borderRadius: "8px" }}>
            {t("results.takeTest")}
          </Button>
        </Stack>
      </Box>

      {/* Bannière résultat frais */}
      {fresh && (() => {
        const fpct = fresh.percentage ?? (fresh.maxScore ? Math.round(fresh.score / fresh.maxScore * 100) : 0);
        const fpassed = fpct >= (exams.find(e => e.id === fresh.exam_id)?.pass_threshold ?? 50);
        return (
          <Alert icon={<EmojiEventsIcon />}
            severity={fpassed ? "success" : "warning"}
            sx={{ mb: 3, borderRadius: "10px", fontWeight: 600, alignItems: "center" }}
            action={
              <Button size="small" component={Link} to="/exams" startIcon={<ReplayIcon />}>
                {t("results.retry")}
              </Button>
            }>
            {t("results.freshResult")} &nbsp;
            <strong>{fresh.score}/{fresh.maxScore}</strong>&nbsp;·&nbsp;
            <strong>{fpct}%</strong>&nbsp;
            {fpassed ? `— ${t("results.wellDone")}` : `— ${t("results.keepPracticing")}`}
          </Alert>
        );
      })()}

      {/* Table */}
      <Paper sx={{ borderRadius: "12px", overflow: "hidden", border: `1px solid ${P.slate[200]}` }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: P.navy }} />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow sx={{ background: P.slate[50] }}>
                  {[t("results.examTitle"), t("results.score"), t("results.result"), t("results.certificate"), t("common.date")].map((h, idx) => (
                    <TableCell key={h} align={idx === 0 ? "left" : idx === 4 ? "right" : "center"}
                      sx={{ fontWeight: 700, color: P.slate[700], fontSize: "0.82rem",
                        textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                      <SchoolIcon sx={{ fontSize: 44, color: P.slate[300], display: "block", mx: "auto", mb: 1.5 }} />
                      <Typography color="text.secondary" fontWeight={600}>
                        {t("results.noResults")}
                      </Typography>
                      <Button variant="contained" component={Link} to="/exams"
                        sx={{ mt: 2, background: P.navy, textTransform: "none", borderRadius: "8px" }}>
                        {t("results.startFirst")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : items.map(a => {
                  const pct    = getPct(a);
                  const passed = pct >= examThreshold(a.exam_id);
                  const col    = pctColor(pct);
                  return (
                    <TableRow key={a.id}
                      sx={{ "&:hover": { background: P.slate[50] }, transition: "background .1s" }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color={P.slate[800]}>
                          {examTitle(a.exam_id)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={a.max_score ? `${a.score}/${a.max_score}` : (a.score ?? "—")}
                          size="small"
                          sx={{ fontWeight: 800, fontSize: "0.8rem",
                            background: alpha(col, .1), color: col }}
                        />
                        <Typography variant="caption"
                          sx={{ display: "block", color: col, fontWeight: 700, mt: .25 }}>
                          {pct}%
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={passed
                            ? <CheckCircleIcon sx={{ fontSize: "0.85rem !important" }} />
                            : <CancelIcon     sx={{ fontSize: "0.85rem !important" }} />}
                          label={passed ? t("common.passed") : t("common.failed")}
                          size="small"
                          sx={{ fontWeight: 700,
                            background: passed ? alpha(P.green, .1) : alpha(P.red, .1),
                            color: passed ? P.green : P.red,
                            "& .MuiChip-icon": { color: passed ? P.green : P.red },
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {passed ? (
                          <Tooltip title={t("results.downloadCert")}>
                            <IconButton size="small" sx={{ color:P.green }}
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem("access_token");
                                  const res = await fetch(
                                    `${process.env.REACT_APP_API_URL || "http://localhost:8001"}/attempts/${a.id}/certificate`,
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  if (!res.ok) throw new Error();
                                  const blob = await res.blob();
                                  const url  = URL.createObjectURL(blob);
                                  const el   = document.createElement("a");
                                  el.href = url; el.download = `certificat_${a.id}.pdf`;
                                  el.click(); URL.revokeObjectURL(url);
                                } catch { alert(t("errors.loadFailed")); }
                              }}>
                              <WorkspacePremiumIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" sx={{ color:P.slate[300] }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" color="text.secondary">
                          {a.submitted_at
                            ? new Date(a.submitted_at).toLocaleDateString(i18n.language === "en" ? "en-US" : "fr-FR", {
                                day: "numeric", month: "short", year: "numeric",
                                hour: "2-digit", minute: "2-digit",
                              })
                            : "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Box sx={{ px: 2, pb: 1 }}>
              <PaginationBar
                page={page} pages={pages} total={total} pageSize={pageSize}
                onPageChange={setPage} onPageSizeChange={setPageSize}
                pageSizeOptions={[5, 10, 25]} loading={loading}
              />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}